import React, { useState, useRef, useEffect } from 'react';
import { MoreOutlined } from '@ant-design/icons';
import { AnyService, ServiceAction } from '../../../types/service.type';
import { useServiceActions } from '../../../hooks/useServiceActions';

interface ActionMenuItem {
  action: ServiceAction;
  label: string;
  icon: React.ReactNode;
  variant?: 'default' | 'danger';
  show?: boolean;
}

interface ServiceActionsMenuProps {
  service: AnyService;
  onActionClick?: (action: ServiceAction) => void;
  customActions?: ActionMenuItem[];
  position?: 'left' | 'right';
  className?: string;
}

const ServiceActionsMenu: React.FC<ServiceActionsMenuProps> = ({
  service,
  onActionClick,
  customActions,
  position = 'right',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  const {
    canStart,
    canStop,
    canRestart,
    canDelete,
    canVisit,
    visitUrl,
    adminUrl,
  } = useServiceActions({ service });

  // Cerrar menú al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Acciones por defecto según el tipo de servicio
  const defaultActions: ActionMenuItem[] = [
    {
      action: 'visit',
      label: 'Visit Site',
      icon: '🌐',
      show: canVisit && !!visitUrl,
    },
    {
      action: 'admin',
      label: 'Admin Panel',
      icon: '⚙️',
      show: !!adminUrl,
    },
    {
      action: 'start',
      label: 'Start Service',
      icon: '▶️',
      show: canStart,
    },
    {
      action: 'stop',
      label: 'Stop Service',
      icon: '⏸️',
      show: canStop,
    },
    {
      action: 'restart',
      label: 'Restart',
      icon: '🔄',
      show: canRestart,
    },
    {
      action: 'logs',
      label: 'View Logs',
      icon: '📄',
      show: true,
    },
    {
      action: 'metrics',
      label: 'Metrics',
      icon: '📊',
      show: true,
    },
    {
      action: 'settings',
      label: 'Settings',
      icon: '⚙️',
      show: true,
    },
    {
      action: 'delete',
      label: 'Delete Service',
      icon: '🗑️',
      variant: 'danger',
      show: canDelete,
    },
  ];

  const actions = customActions || defaultActions.filter(a => a.show !== false);

  const handleActionClick = (action: ServiceAction) => {
    setIsOpen(false);
    onActionClick?.(action);
  };

  const positionClasses = {
    left: 'left-0',
    right: 'right-0',
  };

  return (
    <div ref={menuRef} className={`relative ${className}`}>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        title="More actions"
      >
        <MoreOutlined className="text-lg" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          
          {/* Menu */}
          <div
            className={`
              absolute ${positionClasses[position]} mt-2 w-56
              bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700
              py-1 z-20 animate-fade-in
            `}
          >
            {actions.map((item, index) => (
              <React.Fragment key={item.action}>
                {/* Separador antes de delete */}
                {item.variant === 'danger' && index > 0 && (
                  <hr className="my-1 border-gray-200 dark:border-gray-700" />
                )}
                
                <button
                  onClick={() => handleActionClick(item.action)}
                  className={`
                    w-full text-left px-4 py-2 text-sm
                    flex items-center gap-3
                    transition-colors
                    ${
                      item.variant === 'danger'
                        ? 'text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }
                  `}
                >
                  <span className="text-base">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              </React.Fragment>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ServiceActionsMenu;